import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  ErrorDisplayUnsavedItems,
  ProducePreviewOverlapLoadingOverlay,
  ProductionPreviewDetails,
} from "@ryde/controls";
import { useConfirmDirtyField } from "@ryde/redux";
import {
  APP_VERSION,
  bookingsProductionDrawerConfigurationAtom,
  BulkProduceTypeEnum,
  resolveProduceDirectionsServicePayload,
} from "@ryde/shared";

import { DrawerContentStyled } from "../../drawer";
import { useProductionPreviewDrawer } from "../../production-preview/use-production-preview-drawer";
import { IBookingsProductionStepsComponentProps } from "../bookings-production-steps";

export const BookingsProductionDrawerProducePreview: FC<
  IBookingsProductionStepsComponentProps
> = ({
  onCloseDrawer,
  setHandlers,
  setIsSaveDisabled,
  setCancelLabel,
  setSaveLabel,
  isRedirectToProducedRidesAfterProductionFinish = true,
  resolveFn,
}) => {
  const { t } = useTranslation();
  const bookingsProductionDrawerConfiguration = useRecoilValue(
    bookingsProductionDrawerConfigurationAtom,
  );
  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems unsavedItemText="" />,
  });
  const productionPayload = useMemo(
    () =>
      bookingsProductionDrawerConfiguration
        ? {
            action: bookingsProductionDrawerConfiguration.action,
            productionType:
              bookingsProductionDrawerConfiguration.productionType,
            criteria: bookingsProductionDrawerConfiguration.criteria,
            rideTypeId: bookingsProductionDrawerConfiguration.rideTypeId,
            assignDocksDuringProduction:
              bookingsProductionDrawerConfiguration.assignDocksDuringProduction,
            ...resolveProduceDirectionsServicePayload(
              bookingsProductionDrawerConfiguration.directionsService,
            ),
            frontendVersion: APP_VERSION,
          }
        : null,
    [bookingsProductionDrawerConfiguration],
  );
  const previewAction =
    bookingsProductionDrawerConfiguration?.action ??
    BulkProduceTypeEnum.Produce;
  const {
    isStarted,
    isAborting,
    generalInfo,
    courseSchedulePreviews,
    hubBookingsPreview,
    hubBookingsPreviewSummary,
    hubAutoAssignedBookingIds,
    hubBookingsExcludedWithoutHubTypeIds,
    isPreviewLoading,
    shouldUseDrawerLoader,
    getOverlapLoadingCaption,
    groupedConflicts,
    conflicts,
    isOtherProductionInProgress,
    handleProductionAbort,
    handlePreviousProductionClick,
    loadProductionPreview,
    onCloseHandler,
    onSaveClick,
    saveLabel,
    cancelLabel,
    isSaveDisabled,
  } = useProductionPreviewDrawer({
    productionPayload,
    onCloseDrawer,
    resolveFn,
    action: previewAction,
    isRedirectToProducedRidesAfterProductionFinish,
    labels: {
      startProduction: t("general.PRODUCE"),
      goToRides: t("general.GO_TO_RIDES"),
    },
  });
  const onCloseConfirmed = applyIfConfirmed({ isDirty: true })((): void => {
    onCloseHandler();
  });
  useEffect(() => {
    loadProductionPreview();
  }, [loadProductionPreview]);
  useEffect(() => {
    setHandlers({
      onSaveClick,
      onCloseHandler: onCloseConfirmed,
    });
  }, [onCloseConfirmed, onSaveClick, setHandlers]);
  useEffect(() => {
    setSaveLabel(saveLabel);
  }, [saveLabel, setSaveLabel]);
  useEffect(() => {
    setIsSaveDisabled(isSaveDisabled);
  }, [isSaveDisabled, setIsSaveDisabled]);
  useEffect(() => {
    setCancelLabel(cancelLabel);
  }, [cancelLabel, setCancelLabel]);
  return (
    <DrawerContentStyled className="produce-preview__content">
      {isPreviewLoading && shouldUseDrawerLoader && (
        <ProducePreviewOverlapLoadingOverlay
          caption={getOverlapLoadingCaption()}
        />
      )}
      {productionPayload !== null && (
        <ProductionPreviewDetails
          generalInfo={generalInfo}
          isStarted={isStarted}
          productionPayload={productionPayload}
          conflicts={conflicts}
          loadProductionPreview={loadProductionPreview}
          groupedConflicts={groupedConflicts}
          handlePreviousProductionClick={handlePreviousProductionClick}
          handleProductionAbort={handleProductionAbort}
          isOtherProductionInProgress={isOtherProductionInProgress}
          summaryItems={bookingsProductionDrawerConfiguration?.summaryItems}
          isAborting={isAborting}
          courseSchedulePreviews={courseSchedulePreviews}
          hubBookingsPreview={hubBookingsPreview}
          hubBookingsPreviewSummary={hubBookingsPreviewSummary}
          hubAutoAssignedBookingIds={hubAutoAssignedBookingIds}
          hubBookingsExcludedWithoutHubTypeIds={
            hubBookingsExcludedWithoutHubTypeIds
          }
        />
      )}
    </DrawerContentStyled>
  );
};
